import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import type { PaginatedResponse } from '../types';

// ── Types ──

export type ReportType = 'entity' | 'network' | 'timeline' | 'risk_assessment' | 'investigation';
export type ReportStatus = 'queued' | 'generating' | 'completed' | 'failed';
export type ExportFormat = 'pdf' | 'json' | 'csv';

export interface ReportSummary {
  id: string;
  title: string;
  type: ReportType;
  status: ReportStatus;
  format: ExportFormat;
  entity_ids: string[];
  author: string;
  file_size?: number;
  created_at: string;
  completed_at: string | null;
}

export interface GenerateReportParams {
  title: string;
  type: ReportType;
  format: ExportFormat;
  entity_ids?: string[];
  date_range?: { from: string; to: string };
  options?: Record<string, unknown>;
}

// ── API Functions ──

async function fetchReports(params?: {
  page?: number;
  page_size?: number;
  type?: ReportType;
  status?: ReportStatus;
}): Promise<PaginatedResponse<ReportSummary>> {
  const { data } = await apiClient.get('/reports', { params });
  return data;
}

async function fetchReport(id: string): Promise<ReportSummary & { content?: string }> {
  const { data } = await apiClient.get(`/reports/${id}`);
  return data;
}

async function generateReport(params: GenerateReportParams): Promise<ReportSummary> {
  // Backend has per-type endpoints: /reports/entity/{id}, /reports/network, /reports/timeline
  let endpoint = '/reports/timeline';
  const body: Record<string, unknown> = {
    title: params.title,
    format: params.format,
    options: params.options,
  };

  if (params.type === 'entity' && params.entity_ids?.length) {
    endpoint = `/reports/entity/${params.entity_ids[0]}`;
    body.include_relationships = true;
    body.include_risk_factors = true;
  } else if (params.type === 'network') {
    endpoint = '/reports/network';
    body.entity_ids = params.entity_ids || [];
    body.depth = 2;
  } else {
    // timeline or others
    body.entity_ids = params.entity_ids || [];
    body.date_range = params.date_range;
  }

  const { data } = await apiClient.post(endpoint, body);
  return data;
}

async function downloadReport(id: string, _format: ExportFormat): Promise<Blob> {
  // Backend GET /reports/{id} returns the report content directly
  const { data } = await apiClient.get(`/reports/${id}`, {
    responseType: 'blob',
  });
  return data;
}

async function deleteReport(_id: string): Promise<void> {
  // Backend does not have a delete endpoint — no-op
  console.warn('Report deletion not supported by backend');
}

// ── React Query Hooks ──

export function useReports(params?: {
  page?: number;
  page_size?: number;
  type?: ReportType;
  status?: ReportStatus;
}) {
  return useQuery({
    queryKey: ['reports', params],
    queryFn: () => fetchReports(params),
    staleTime: 30_000,
  });
}

export function useReport(id: string | null) {
  return useQuery({
    queryKey: ['report', id],
    queryFn: () => fetchReport(id!),
    enabled: !!id,
  });
}

export function useGenerateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

export function useDownloadReport() {
  return useMutation({
    mutationFn: ({ id, format }: { id: string; format: ExportFormat }) =>
      downloadReport(id, format),
    onSuccess: (blob, { id, format }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${id}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}
